import { type PipelineCard } from '@/pages/pipelines/common/types';

export const NEW_INVESTOR_PARENT_STAGE = 'LinkClicked';

const RAW_TYPE_BADGE_LABELS = new Set([
  'None',
  'direct',
  'ExistingInvestor',
  'NewInvestor',
  'DualNewAndExisting',
]);
const NEW_INVESTOR_PARENT_BADGE_LABELS = new Set(['NewInvestor', 'NI parent']);
const PRE_SUBSCRIPTION_INDICATOR_STAGES = new Set(['LinkClicked', 'DueDiligenceViewed']);
const PRE_SUBSCRIPTION_INDICATOR_KEYS = new Set([
  'following',
  'followingFund',
  'communication',
  'inCommunication',
]);
const DIRECT_INTRODUCTION_TYPES = new Set(['None', 'direct']);
const EXISTING_INVESTOR_TYPES = new Set(['ExistingInvestor', 'EI']);
const NEW_INVESTOR_TYPES = new Set(['NewInvestor', 'NI parent', 'NI child']);
const DUAL_INVESTOR_TYPES = new Set(['DualNewAndExisting', 'NI + EI']);
export const normalizeInvestmentCardPresentation = <TCard extends PipelineCard>(
  card: TCard,
  options: { faceIndicatorsOnly?: boolean } = {},
): TCard => {
  let title = card.title;
  let variant = card.variant;
  let titleTone = card.titleTone;
  let accentTone = card.accentTone;
  let mixedTone = card.mixedTone;

  const badges = card.badges.flatMap((badge) => {
    if (badge.key !== 'type' || !RAW_TYPE_BADGE_LABELS.has(badge.label)) return badge;

    if (badge.label === 'None' || badge.label === 'direct') {
      variant = variant ?? 'dashed';
      accentTone = accentTone ?? 'neutral';

      return [];
    }

    if (badge.label === 'ExistingInvestor') {
      accentTone = accentTone ?? 'blue';

      return {
        ...badge,
        label: 'EI',
        tone: 'blue' as const,
        variant: 'filled' as const,
      };
    }

    if (badge.label === 'DualNewAndExisting') {
      accentTone = accentTone ?? 'amber';
      mixedTone = true;

      return {
        ...badge,
        label: 'NI + EI',
        tone: 'amber' as const,
        variant: 'filled' as const,
      };
    }

    if (isPipelineNewInvestorParentCard(card, titleTone)) {
      title = 'No fund yet';
      variant = variant ?? 'dashed';
      titleTone = 'amber';
      accentTone = accentTone ?? 'amber';

      return {
        ...badge,
        label: 'NI parent',
        tone: 'amber' as const,
        variant: 'filled' as const,
      };
    }

    accentTone = accentTone ?? 'amber';

    return {
      ...badge,
      label: 'NI child',
      tone: 'amber' as const,
      variant: 'filled' as const,
    };
  });

  return {
    ...card,
    title,
    variant,
    titleTone,
    accentTone,
    mixedTone,
    badges,
    indicators: options.faceIndicatorsOnly
      ? getInvestmentCardFaceIndicators(card)
      : card.indicators,
  };
};

export const normalizeNewInvestorParentStage = <TCard extends PipelineCard>(
  card: TCard,
): TCard => ({
  ...card,
  stage: NEW_INVESTOR_PARENT_STAGE,
});

export const isPipelineNewInvestorParentCard = (
  card: PipelineCard,
  titleTone = card.titleTone ?? 'default',
) => {
  const typeBadge = card.badges.find((badge) => badge.key === 'type');
  if (!typeBadge || !NEW_INVESTOR_PARENT_BADGE_LABELS.has(typeBadge.label)) return false;

  return (
    card.stage === NEW_INVESTOR_PARENT_STAGE || !card.entityRefs.fundId || titleTone === 'amber'
  );
};

export const isDirectInvestmentCard = (card: PipelineCard) =>
  getInvestmentIntroductionTypeMatches(card).has('None');

export const getInvestmentIntroductionTypeMatches = (card: PipelineCard) => {
  const typeBadge = card.badges.find((badge) => badge.key === 'type');
  const label = typeBadge?.label;
  const matches = new Set<string>();

  if (!label || DIRECT_INTRODUCTION_TYPES.has(label)) {
    matches.add('None');
    return matches;
  }

  if (EXISTING_INVESTOR_TYPES.has(label)) {
    matches.add('ExistingInvestor');
    return matches;
  }

  if (NEW_INVESTOR_TYPES.has(label)) {
    matches.add('NewInvestor');
    return matches;
  }

  if (DUAL_INVESTOR_TYPES.has(label)) {
    matches.add('DualNewAndExisting');
    matches.add('ExistingInvestor');
    matches.add('NewInvestor');
    return matches;
  }

  return matches;
};

const getInvestmentCardFaceIndicators = (card: PipelineCard) =>
  card.indicators.filter((indicator) => {
    if (!indicator.active) return false;

    const stageLimited =
      Boolean(indicator.icon === 'eye' || indicator.icon === 'message') ||
      PRE_SUBSCRIPTION_INDICATOR_KEYS.has(indicator.key);

    if (stageLimited && isPipelineNewInvestorParentCard(card)) return false;

    return !stageLimited || PRE_SUBSCRIPTION_INDICATOR_STAGES.has(card.stage);
  });
